import { Button, Header, Link, SelectProps, SpaceBetween } from "@amzn/awsui-components-react";
import { Button as UxmButton } from "@amzn/uxm-aws-ui-components-react/lib/src/polaris/button";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { CustomFormFieldGroup, CustomFormFieldTypes } from "interfaces";
import CustomForm from "../CustomForm/CustomForm";
import { useFinanceCodesForSelect } from "hooks/useFinanceCodes";
import { useBuildingsForSelect } from "hooks/useBuildings";
import { getOrderFormStructure, orderConfirmationFormIds } from "./FormStructure";
import { useEffect, useState } from "react";
import { SUBMIT_ORDER_TEST_ID } from "helpers";

const getStringPath: string = "common.component.LocalItOrderConfirmationContent.orderForm";
const getStringFinancePath: string = `${getStringPath}.financeFormSection`;
const getStringPathLinks: string = "links";

/**
 * Component that renders the finance form header section
 *
 * @returns {JSX.Element} Header component for finance form
 */
export const FinanceFormHeader = () => (
    <Header
        variant="h2"
        description={
            <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <span style={{ color: "var(--color-text-status-error, #d91515)" }}>
                    {getString(`${getStringFinancePath}.text`)}
                </span>
                <Link href={getString(`${getStringPathLinks}.financeFormLink`)} external>
                    {getString(`${getStringFinancePath}.link`)}
                </Link>
            </span>
        }
    >
        {getString(`${getStringFinancePath}.header`)}
    </Header>
);

/**
 * Props for the order information form component
 * @interface
 * @property {Map<string, CustomFormFieldTypes>} formValues - Map containing the current form field values
 * @property {React.Dispatch<React.SetStateAction<Map<string, CustomFormFieldTypes>>>} setFormValues - State setter function for form values
 * @property {(id: string) => string | undefined} getHasError - Function that checks if a field has validation errors
 * @property {React.FormEventHandler<HTMLFormElement>} customOnSubmitForm - Custom form submission handler
 * @property {boolean} loading - Loading state indicator for form submission
 */
export interface OrderInformationFormProps {
    formValues: Map<string, CustomFormFieldTypes>;
    setFormValues: React.Dispatch<React.SetStateAction<Map<string, CustomFormFieldTypes>>>;
    getHasError: (id: string) => string | undefined;
    customOnSubmitForm: React.FormEventHandler<HTMLFormElement>;
    loading: boolean;
    goBackToHardwareRestockPage: () => void;
}

/**
 * Main order information form component that handles order submission
 * This component generates the form for the order confirmation
 *
 * @returns {JSX.Element} Complete order information form with sections and validation
 */
const OrderInformationForm = ({
    getHasError,
    customOnSubmitForm,
    formValues,
    setFormValues,
    loading,
    goBackToHardwareRestockPage,
}: OrderInformationFormProps) => {
    /**
     * Available finance codes
     */
    const financeCodes: SelectProps.Options = useFinanceCodesForSelect();

    /**
     * Available building codes
     */
    const deliverTos: SelectProps.Options = useBuildingsForSelect();

    /**
     * Function that determines if expedite is toggle
     *
     * @returns {boolean} returns if the expedite option is toggled
     */
    const expediteOpen = (): boolean => {
        const expediteToggle = formValues.get(orderConfirmationFormIds.expedite);

        return expediteToggle as boolean;
    };

    const [onBehalf, setOnBehalf] = useState<string>("");

    useEffect(() => {
        setOnBehalf((formValues.get(orderConfirmationFormIds.behalfUser) as string) || "");
    }, [formValues]);

    const formOptions = getOrderFormStructure({
        financeCodes,
        deliverTos,
        expediteOpen,
        getHasError,
        onBehalf,
    });

    /**
     * Structure for the order confirmation form
     */
    const orderInformationSections: CustomFormFieldGroup[] = [
        /**
         * General Order Information Section
         */
        {
            formInputs: [
                formOptions.behalfUser,
                formOptions.costCenter,
                formOptions.companyGLCode,
                formOptions.locationGLCode,
                formOptions.financeCode,
                formOptions.project,
                formOptions.expedite,
                formOptions.expediteDate,
                formOptions.deliverTo,
                formOptions.justification,
                formOptions.watchers,
            ],
        },
        /**
         * Finance Form Section
         */
        {
            header: <FinanceFormHeader />,
            formInputs: [formOptions.financeForm],
        },
    ];

    return (
        <>
            <CustomForm
                formValues={formValues}
                setFormValues={setFormValues}
                onSubmit={customOnSubmitForm}
                header={<Header>{getString(`${getStringPath}.title`)}</Header>}
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <UxmButton
                            disabled={loading}
                            formAction="none"
                            variant="link"
                            onClick={goBackToHardwareRestockPage}
                            touchpoint="back-to-order-page"
                        >
                            {getString(`${getStringPath}.backButton`)}
                        </UxmButton>
                        <UxmButton
                            disabled={loading}
                            loading={loading}
                            variant="primary"
                            data-testid={SUBMIT_ORDER_TEST_ID}
                            touchpoint="submit-order"
                        >
                            {getString(`${getStringPath}.submitButton`)}
                        </UxmButton>
                    </SpaceBetween>
                }
                formSections={orderInformationSections}
            />
        </>
    );
};

export default OrderInformationForm;
